export const isUnlimitedInventory = (product) => (
  String(product?.unit || '').trim().toLowerCase() === 'each'
  && Number(product?.stock) === -1
);

export const INVENTORY_QUANTITY_REVIEW_THRESHOLD = 1000;

export const isProductOutOfStock = (product) => {
  if (String(product?.status || '').trim().toLowerCase() === 'out of stock') {
    return true;
  }

  const stock = Number(product?.stock);
  return Number.isFinite(stock) && !isUnlimitedInventory(product) && stock <= 0;
};

export const resolveInventoryStatus = ({ stock, unit, status }) => {
  const product = { stock, unit, status };
  if (!isUnlimitedInventory(product) && Number.isFinite(Number(stock)) && Number(stock) <= 0) {
    return 'Out of Stock';
  }
  if (status) {
    return status;
  }
  if (isUnlimitedInventory(product)) {
    return 'In Stock';
  }
  return Number(stock) < 10 ? 'Low Stock' : 'In Stock';
};

export const getCanonicalInventoryStatus = (product) => resolveInventoryStatus({
  stock: product?.stock,
  unit: product?.unit,
  status: product?.status
});

export const isProductLowStock = (product) => {
  if (isProductOutOfStock(product) || isUnlimitedInventory(product)) return false;
  if (String(product?.status || '').trim().toLowerCase() === 'low stock') return true;

  const stock = Number(product?.stock);
  return Number.isFinite(stock) && stock > 0 && stock < 10;
};

export const isProductOutOfSeason = (product) => (
  String(product?.season_status || '').trim().toLowerCase() === 'out of season'
);

export const hasInventoryImage = (product) => Boolean(
  product?.images?.some(image => String(image?.image_url || '').trim())
);

export const hasQuantityNeedingReview = (product) => {
  if (isUnlimitedInventory(product)) return false;
  const stock = Number(product?.stock);
  return Number.isFinite(stock) && stock >= INVENTORY_QUANTITY_REVIEW_THRESHOLD;
};

export const getInventoryAttentionSignal = (product) => {
  if (isProductOutOfStock(product)) {
    return { key: 'out', label: 'Out of stock', detail: 'Unavailable to shoppers' };
  }
  if (isProductLowStock(product)) {
    return { key: 'low', label: 'Low stock', detail: 'Replenishment recommended' };
  }
  if (isProductOutOfSeason(product)) {
    return { key: 'seasonal', label: 'Out of season', detail: 'Seasonally unavailable' };
  }
  if (hasQuantityNeedingReview(product)) {
    return { key: 'quantity', label: 'Check quantity', detail: 'Unusually high finite stock' };
  }
  if (!hasInventoryImage(product)) {
    return { key: 'image', label: 'Image needed', detail: 'No catalog image' };
  }
  return { key: 'ready', label: 'Shopper ready', detail: 'Available in the catalog' };
};

export const summarizeInventory = (products, totalCount) => {
  const rows = products || [];
  const resolvedTotal = Number(totalCount ?? rows.length) || 0;
  const outOfStock = rows.filter(isProductOutOfStock).length;
  const lowStock = rows.filter(isProductLowStock).length;
  const outOfSeason = rows.filter(isProductOutOfSeason).length;
  const quantityReview = rows.filter(hasQuantityNeedingReview).length;
  const missingImages = rows.filter(product => !hasInventoryImage(product)).length;
  const shopperReady = rows.filter(product => (
    !isProductOutOfStock(product) && !isProductOutOfSeason(product)
  )).length;
  const catalogChecks = rows.filter(product => (
    isProductOutOfSeason(product)
    || hasQuantityNeedingReview(product)
    || !hasInventoryImage(product)
  )).length;
  const categories = new Set(
    rows.map(product => String(product?.category || '').trim()).filter(Boolean)
  ).size;
  const unlimited = rows.filter(isUnlimitedInventory).length;

  return {
    totalCount: resolvedTotal,
    categories,
    unlimited,
    shopperReady,
    shopperReadyRate: resolvedTotal ? Math.round((shopperReady / resolvedTotal) * 100) : 0,
    lowStock,
    outOfStock,
    stockAttention: lowStock + outOfStock,
    outOfSeason,
    quantityReview,
    missingImages,
    catalogChecks
  };
};
